import { useTranslation } from 'react-i18next';

import { useEmail } from '@/components/providers/EmailProvider/EmailProvider';
import ActionChipList from '@/features/compose/components/ActionChipList';
import ChatBubble from '@/features/compose/components/ChatBubble';
import ConceptReplyError from '@/features/compose/components/ConceptReplyError';
import ConceptReplyResult, {
  type ConceptReplyCopyEvent,
  type ConceptReplyRating,
} from '@/features/compose/components/ConceptReplyResult';
import TypingIndicator from '@/features/compose/components/TypingIndicator';
import type {
  ConceptReply,
  ConceptReplyRequest,
} from '@/features/compose/types';
import hasItems from '@/lib/utils/hasItems';

type Props = {
  request: ConceptReplyRequest;
  concept: ConceptReply;
  onRetry: () => Promise<void> | void;
  onCopied: (event: ConceptReplyCopyEvent) => void;
  onRated: (rating: ConceptReplyRating) => void;
};

export default function ConceptReplyConversation({
  request,
  concept,
  onRetry,
  onCopied,
  onRated,
}: Props) {
  const { t } = useTranslation();

  const {
    analysis: { actions },
  } = useEmail();

  const requestedActions = (actions ?? []).filter((action) =>
    request.actions?.includes(action.id),
  );

  const requestBubbleText = hasItems(requestedActions)
    ? t('CONCEPT_REPLY_REQUEST_ACTION', { action: '' }).trim()
    : t('CONCEPT_REPLY_REQUEST_COMPOSE');

  return (
    <div className="flex h-full flex-col gap-2">
      <ChatBubble from="user">
        <p className="leading-6">
          {requestBubbleText}{' '}
          {hasItems(requestedActions) && (
            <ActionChipList actions={requestedActions} />
          )}
        </p>
      </ChatBubble>

      {concept.status === 'generating' && (
        <ChatBubble from="assistant">
          <TypingIndicator />
        </ChatBubble>
      )}

      {concept.status === 'error' && <ConceptReplyError onRetry={onRetry} />}

      {concept.status === 'ready' && (
        <ConceptReplyResult
          initialText={concept.text}
          onCopied={onCopied}
          onRated={onRated}
        />
      )}
    </div>
  );
}
